import { useState, useEffect, useCallback } from 'react';
import { img, readdy, portraitAt } from '@/utils/images';
import { fetchFoundingMembers, type FoundingMember } from '@/utils/adminStore';
import { executiveTeam } from '@/mocks/expertsData';
import ScrollReveal from '@/components/base/ScrollReveal';

interface FoundingUI {
  name: string;
  title: string;
  bio: string;
  tags: string[];
  imagePrompt: string;
  imageUrl: string;
}

// ====== Cache ======
const CACHE_KEY = 'mgmt_executive_team_cache';
const CACHE_TTL = 10 * 60 * 1000;

function getCached(): FoundingUI[] | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (Date.now() - entry.timestamp > CACHE_TTL) return null;
    if (!Array.isArray(entry.data) || entry.data.length === 0) return null;
    return entry.data;
  } catch {
    return null;
  }
}

function setCache(data: FoundingUI[]) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
  } catch { /* silently ignore */ }
}

function mapMock(): FoundingUI[] {
  return executiveTeam.map((m) => ({
    name: m.name,
    title: m.title,
    bio: m.bio || '',
    tags: m.tags || [],
    imagePrompt: m.imagePrompt || '',
    imageUrl: m.imageUrl || '',
  }));
}

function mapSupabase(data: FoundingMember[]): FoundingUI[] {
  return data.map((m: FoundingMember) => ({
    name: m.name,
    title: m.title,
    bio: m.bio || '',
    tags: Array.isArray(m.tags) ? m.tags : [],
    imagePrompt: m.image_prompt || '',
    imageUrl: m.image_url || '',
  }));
}

export default function ExecutiveTeam() {
  // Start with cache or mock — content appears instantly
  const [members, setMembers] = useState<FoundingUI[]>(() => {
    const cached = getCached();
    if (cached) return cached;
    return mapMock();
  });

  const loadRemote = useCallback(async () => {
    try {
      const data = await fetchFoundingMembers();
      if (data && data.length > 0) {
        const ui = mapSupabase(data);
        setMembers(ui);
        setCache(ui);
      }
    } catch {
      // keep current data (mock or cache)
    }
  }, []);

  useEffect(() => {
    loadRemote();
  }, [loadRemote]);

  return (
    <section className="py-16 md:py-24" style={{ backgroundColor: '#f6f3ee' }}>
      <div className="px-6 md:px-10">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal variant="fade-up">
            <div className="text-center mb-12">
              <p className="text-brand-brown text-sm tracking-widest mb-3 font-medium">
                创始团队
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-brand-deep leading-tight">
                资深高管团队，掌舵品牌航向
              </h2>
              <p className="text-gray-500 text-sm mt-3 max-w-xl mx-auto">
                汇聚行业顶尖管理人才，以前瞻视野与丰富经验，驱动品牌战略落地与全国市场布局
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {members.map((member, i) => (
              <ScrollReveal key={member.name} variant="scale-up" delay={i * 120}>
                <div className="bg-white rounded-2xl overflow-hidden border border-brand-light/60 group">
                  <div className="relative aspect-[3/4] overflow-hidden bg-brand-light/40">
                    <img
                      src={member.imageUrl || portraitAt(i)}
                      alt={member.name}
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-lg md:text-xl font-bold text-white leading-tight">
                        {member.name}
                      </h3>
                      <p className="text-white/70 text-sm mt-0.5">
                        {member.title}
                      </p>
                    </div>
                  </div>
                  <div className="p-4 md:p-5">
                    <div className="relative group/bio">
                      <p className="text-gray-500 text-sm leading-relaxed line-clamp-4 cursor-default">
                        {member.bio}
                      </p>
                      <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-80 max-w-[calc(100vw-2rem)] bg-gray-900 text-white text-xs leading-relaxed p-4 rounded-xl opacity-0 invisible group-hover/bio:opacity-100 group-hover/bio:visible transition-all duration-200 z-50 pointer-events-none shadow-xl">
                        {member.bio}
                        <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-transparent border-t-gray-900" />
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}